import { formatDuration } from '@vgtv/time-helpers';
import cn from 'classnames';
import { FunctionComponent, JSX } from 'preact';
import { useCallback, useState } from 'preact/hooks';

import { useTranslation } from '../../../../utils/useTranslation';
import { Button } from '../Button/Button';
import { useIcons } from '../../hooks/useIcons';
import { usePlayer } from '../../hooks/PlayerProvider';
import { useJWPlayerEvent } from '../../hooks/useJWPlayerEvent';
import { getDvrSeekLimit, useSeek } from '../../hooks/useSeek';
import ns from '../../translations';
import { ButtonUnstyled } from '../ButtonUnstyled/ButtonUnstyled';
import { LiveBadge } from '../LiveBadge/LiveBadge';
import { Typography, TypographyProps } from '../Typography/Typography';

const LIVE_THRESHOLD = -15; // seconds

const isDvrPositionLive = (position: number, dvrSeekLimit: number) => {
  const dvrThreshold =
    -dvrSeekLimit > LIVE_THRESHOLD ? LIVE_THRESHOLD : -dvrSeekLimit;

  return Math.ceil(position) >= dvrThreshold;
};

interface LiveTimeDisplayProps {
  variant?: 'default' | 'podcast';
  color?: TypographyProps['color'];
}

export const LiveTimeDisplay: FunctionComponent<LiveTimeDisplayProps> = ({
  variant = 'default',
  color = 'white',
}) => {
  const player = usePlayer();
  const { seekToLive } = useSeek();
  const { t } = useTranslation(ns);
  const { Bullet } = useIcons();

  const [position, setPosition] = useState(player.getCurrentTime());

  const handleTime = useCallback((param: jwplayer.TimeParam) => {
    setPosition(param.position);
  }, []);

  useJWPlayerEvent('time', handleTime);

  const isDvrLive = isDvrPositionLive(
    position,
    getDvrSeekLimit(player.getJWPlayer())
  );

  const handleClick = useCallback(() => {
    if (!isDvrLive) {
      seekToLive();
    }
  }, [isDvrLive, seekToLive]);

  const handleKeyDown: JSX.KeyboardEventHandler<HTMLSpanElement> = useCallback(
    (e) => {
      if (e.key === 'Space' || e.key === 'Enter') {
        handleClick();
      }
    },
    [handleClick]
  );

  const timeLabel = `-${formatDuration(Math.abs(position * 1000))}`;

  if (variant === 'podcast') {
    return (
      <Button
        color={isDvrLive ? 'accent' : 'secondary'}
        size="xsmall"
        className={cn('vgp-podcast-layout-live-badge', {
          'vgp-podcast-layout-live-badge--live': !isDvrLive,
        })}
        aria-disabled={isDvrLive}
        onClick={handleClick}
        startIcon={<Bullet />}
      >
        {isDvrLive ? t('LIVE') : timeLabel}
      </Button>
    );
  }

  return (
    <div className="vgp-time-display">
      <ButtonUnstyled
        component="span"
        className="vgp-time-display-live"
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        aria-disabled={isDvrLive}
      >
        {isDvrLive ? (
          <Typography
            className="vgp-time-display-live-badge"
            variant="label"
            size="medium"
            color={color}
            data-skin="breaking"
          >
            <LiveBadge />
            {t('Live')}
          </Typography>
        ) : (
          <Typography
            className="vgp-time-display-time-badge"
            variant="label"
            size="medium"
            color={color}
          >
            <LiveBadge disabled />
            {timeLabel}
          </Typography>
        )}
      </ButtonUnstyled>
    </div>
  );
};
